import { Avatar, AvatarFallback, AvatarImage } from "@bowfast/ui/components/avatar";
import type { BadgeProps } from "@bowfast/ui/components/badge";
import { Badge } from "@bowfast/ui/components/badge";
import { Button } from "@bowfast/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@bowfast/ui/components/card";
import { Separator } from "@bowfast/ui/components/separator";
import { cn } from "@bowfast/ui/lib/utils";
import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
  ActivityIcon,
  AnchorIcon,
  ArrowRightIcon,
  AwardIcon,
  CalendarIcon,
  CloudIcon,
  CompassIcon,
  DropletIcon,
  FlagIcon,
  GaugeIcon,
  MapPinIcon,
  Navigation2Icon,
  PlusIcon,
  SailboatIcon,
  ThermometerIcon,
  TrendingUpIcon,
  UsersIcon,
  WindIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { StatItem } from "~/lib/types";

// ── Animation presets ─────────────────────────────────────────────────────────

const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, ease: EASE_OUT },
};

const stagger = {
  animate: { transition: { staggerChildren: 0.06 } },
};

// ── Animated counter ──────────────────────────────────────────────────────────

function AnimatedNumber({ value, duration = 1.2 }: { value: number; duration?: number }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef<number>(0);

  useEffect(() => {
    const start = ref.current;
    const diff = value - start;
    const startTime = performance.now();

    function tick(now: number) {
      const elapsed = Math.min((now - startTime) / (duration * 1000), 1);
      const eased = 1 - (1 - elapsed) ** 3;
      const current = Math.round(start + diff * eased);
      setDisplay(current);
      if (elapsed < 1) requestAnimationFrame(tick);
      else ref.current = value;
    }
    requestAnimationFrame(tick);
  }, [value, duration]);

  return <>{display.toLocaleString()}</>;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function greeting(name: string): string {
  const h = new Date().getHours();
  if (h < 5 || h >= 21) return `Night watch, ${name}`;
  if (h < 12) return `Good morning, ${name}`;
  if (h < 17) return `Good afternoon, ${name}`;
  return `Good evening, ${name}`;
}

// ── Mock data (replace with GraphQL: DashboardExpeditions + DashboardOrg + DashboardMe) ───

const USER_NAME = "Dan";
const ORG_NAME = "SY Wanderer Sailing Club";

const ACTIVE = {
  slug: "atlantic-crossing-2026",
  name: "Atlantic Crossing 2026",
  kind: "Offshore Passage",
  departurePort: "Las Palmas de Gran Canaria",
  arrivalPort: "Bridgetown, Barbados",
  departureDate: new Date("2026-04-10"),
  returnDate: new Date("2026-05-01"),
  vessel: { flag: "🇵🇹", name: "SY Aventura II" },
  distanceCovered: 2340,
  totalDistance: 2700,
  crew: [{ initials: "DM" }, { initials: "MS" }, { initials: "JP" }, { initials: "CL" }],
  crewOverflow: 2,
};

// ── KPI config ────────────────────────────────────────────────────────────────

interface KPIData {
  label: string;
  value: number;
  unit: string;
  delta: string;
  Icon: LucideIcon;
  accentClass: string;
  iconBg: string;
  iconColor: string;
}

const KPI_DATA: KPIData[] = [
  {
    label: "Miles Sailed",
    value: 12847,
    unit: "nm total",
    delta: "+1,240 this year",
    Icon: Navigation2Icon,
    accentClass: "bg-abyss-500",
    iconBg: "bg-abyss-50 dark:bg-abyss-800",
    iconColor: "text-abyss-500",
  },
  {
    label: "Certificates",
    value: 43,
    unit: "issued",
    delta: "+7 this year",
    Icon: AwardIcon,
    accentClass: "bg-shallow-500",
    iconBg: "bg-shallow-50 dark:bg-shallow-900",
    iconColor: "text-shallow-600",
  },
  {
    label: "Active Crew",
    value: 18,
    unit: "members",
    delta: "4 on next voyage",
    Icon: UsersIcon,
    accentClass: "bg-signal-500",
    iconBg: "bg-signal-50 dark:bg-signal-900",
    iconColor: "text-signal-600",
  },
  {
    label: "Expeditions",
    value: 12,
    unit: "completed",
    delta: "3 in pipeline",
    Icon: CompassIcon,
    accentClass: "bg-violet-500",
    iconBg: "bg-violet-50 dark:bg-violet-900",
    iconColor: "text-violet-600",
  },
];

// ── Weather data ──────────────────────────────────────────────────────────────

interface WeatherPoint extends StatItem {
  unit: string;
  color: string;
}

const WEATHER: WeatherPoint[] = [
  { label: "Wind", Icon: WindIcon, value: "18", unit: "kts NE", color: "text-abyss-400" },
  { label: "Waves", Icon: DropletIcon, value: "1.8", unit: "m", color: "text-shallow-500" },
  { label: "Temp", Icon: ThermometerIcon, value: "24", unit: "°C", color: "text-signal-500" },
  { label: "Pressure", Icon: GaugeIcon, value: "1018", unit: "hPa", color: "text-abyss-300" },
  { label: "Cloud", Icon: CloudIcon, value: "35", unit: "%", color: "text-muted-foreground" },
];

// ── Quick actions config ──────────────────────────────────────────────────────

interface QuickAction {
  Icon: LucideIcon;
  label: string;
  color: string;
  bg: string;
  to: string;
}

const QUICK_ACTIONS: QuickAction[] = [
  {
    Icon: PlusIcon,
    label: "Plan a voyage",
    color: "text-white",
    bg: "bg-abyss-500 hover:bg-abyss-400",
    to: "/expeditions/new",
  },
  {
    Icon: UsersIcon,
    label: "Invite crew",
    color: "text-abyss-500",
    bg: "bg-abyss-50 hover:bg-abyss-100 dark:bg-abyss-800 dark:hover:bg-abyss-700 dark:text-abyss-300",
    to: "/crew",
  },
  {
    Icon: CalendarIcon,
    label: "Log miles",
    color: "text-shallow-600",
    bg: "bg-shallow-50 hover:bg-shallow-100 dark:bg-shallow-900 dark:hover:bg-shallow-800 dark:text-shallow-400",
    to: "/expeditions",
  },
  {
    Icon: AwardIcon,
    label: "Issue cert",
    color: "text-signal-600",
    bg: "bg-signal-50 hover:bg-signal-100 dark:bg-signal-900 dark:hover:bg-signal-800 dark:text-signal-400",
    to: "/certificates",
  },
];

// ── Voyage data ───────────────────────────────────────────────────────────────

interface Voyage {
  slug: string;
  name: string;
  route: string;
  dates: string;
  status: "open" | "planning";
  crewFilled: number;
  crewTotal: number;
  vessel: string;
  flag: string;
  distance: string;
}

const VOYAGES: Voyage[] = [
  {
    slug: "azores-passage-2026",
    name: "Azores Passage",
    route: "Lisbon → Horta",
    dates: "14–19 May",
    status: "open",
    crewFilled: 4,
    crewTotal: 8,
    vessel: "SY Blue Horizon",
    flag: "🇵🇹",
    distance: "990 nm",
  },
  {
    slug: "west-med-cruise-2026",
    name: "West Med Cruise",
    route: "Palma → Dubrovnik",
    dates: "8–26 Jun",
    status: "planning",
    crewFilled: 0,
    crewTotal: 10,
    vessel: "SY Meridian",
    flag: "🇪🇸",
    distance: "1,450 nm",
  },
];

// ── Fleet data ────────────────────────────────────────────────────────────────

type VesselStatus = "at-sea" | "in-port" | "maintenance";

interface FleetVessel {
  name: string;
  type: string;
  status: VesselStatus;
  expedition?: string;
}

const FLEET: FleetVessel[] = [
  { name: "SY Aventura II", type: "Beneteau Oceanis 50", status: "at-sea", expedition: "Atlantic Crossing" },
  { name: "SY Blue Horizon", type: "Jeanneau Sun Odyssey 49DS", status: "in-port" },
  { name: "SY Meridian", type: "Hallberg-Rassy 46", status: "maintenance" },
];

const vesselConfig: Record<
  VesselStatus,
  { label: string; variant: BadgeProps["variant"]; iconBg: string; iconColor: string }
> = {
  "at-sea": {
    label: "At sea",
    variant: "active",
    iconBg: "bg-shallow-100 dark:bg-shallow-900",
    iconColor: "text-shallow-600",
  },
  "in-port": {
    label: "In port",
    variant: "confirmed",
    iconBg: "bg-abyss-50 dark:bg-abyss-800",
    iconColor: "text-abyss-500",
  },
  maintenance: {
    label: "Maintenance",
    variant: "draft",
    iconBg: "bg-signal-50 dark:bg-signal-900",
    iconColor: "text-signal-600",
  },
};

// ── Crew data ─────────────────────────────────────────────────────────────────

type CrewPresence = "at-sea" | "available" | "pending";

interface CrewMember {
  name: string;
  initials: string;
  role: string;
  cert: string;
  presence: CrewPresence;
  avatar?: string;
}

const CREW: CrewMember[] = [
  {
    name: "Maria Santos",
    initials: "MS",
    role: "First Mate",
    cert: "Yachtmaster Offshore",
    presence: "at-sea",
    avatar: "https://api.dicebear.com/9.x/notionists/svg?seed=Maria",
  },
  {
    name: "João Pereira",
    initials: "JP",
    role: "Crew",
    cert: "Day Skipper",
    presence: "at-sea",
    avatar: "https://api.dicebear.com/9.x/notionists/svg?seed=Joao",
  },
  {
    name: "Luís Faria",
    initials: "LF",
    role: "Crew",
    cert: "Competent Crew",
    presence: "available",
    avatar: "https://api.dicebear.com/9.x/notionists/svg?seed=Luis",
  },
  {
    name: "Ana Coelho",
    initials: "AC",
    role: "Student",
    cert: "Start Sailing",
    presence: "pending",
    avatar: "https://api.dicebear.com/9.x/notionists/svg?seed=Ana",
  },
  {
    name: "Rui Gonçalves",
    initials: "RG",
    role: "Crew",
    cert: "Coastal Skipper",
    presence: "available",
    avatar: "https://api.dicebear.com/9.x/notionists/svg?seed=Rui",
  },
];

const presenceConfig: Record<CrewPresence, { dot: string; label: string }> = {
  "at-sea": { dot: "bg-shallow-500", label: "At sea" },
  available: { dot: "bg-shallow-400", label: "Available" },
  pending: { dot: "bg-signal-500", label: "Pending" },
};

// ── Activity data ─────────────────────────────────────────────────────────────

interface Activity {
  id: string;
  Icon: LucideIcon;
  iconColor: string;
  iconBg: string;
  text: string;
  time: string;
}

const ACTIVITIES: Activity[] = [
  {
    id: "1",
    Icon: UsersIcon,
    iconColor: "text-shallow-600",
    iconBg: "bg-shallow-100 dark:bg-muted",
    text: "Maria Santos confirmed for Atlantic Crossing",
    time: "2h ago",
  },
  {
    id: "2",
    Icon: AwardIcon,
    iconColor: "text-signal-600",
    iconBg: "bg-signal-100 dark:bg-muted",
    text: "Expedition certificate issued to João Pereira",
    time: "5h ago",
  },
  {
    id: "3",
    Icon: SailboatIcon,
    iconColor: "text-abyss-500",
    iconBg: "bg-abyss-100 dark:bg-muted",
    text: "Azores Passage opened for crew applications",
    time: "1d ago",
  },
  {
    id: "4",
    Icon: ActivityIcon,
    iconColor: "text-coral-500",
    iconBg: "bg-coral-100 dark:bg-muted",
    text: "SY Blue Horizon — Insurance expires in 18 days",
    time: "1d ago",
  },
  {
    id: "5",
    Icon: Navigation2Icon,
    iconColor: "text-abyss-400",
    iconBg: "bg-abyss-50 dark:bg-muted",
    text: "Pedro Rocha reached 500 nm — Coastal Skipper milestone",
    time: "2d ago",
  },
];

// ── Sub-components ────────────────────────────────────────────────────────────

export function ActiveHero() {
  const dayNum = Math.ceil((Date.now() - ACTIVE.departureDate.getTime()) / 86400000);
  const totalDays = Math.ceil((ACTIVE.returnDate.getTime() - ACTIVE.departureDate.getTime()) / 86400000);
  const progressPct = Math.round((ACTIVE.distanceCovered / ACTIVE.totalDistance) * 100);

  return (
    <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.04 }}>
      <Card className="relative overflow-hidden border-0 bg-abyss-900 text-white shadow-xl">
        {/* Ocean-glow overlay */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 85% 50%, rgba(4,124,199,0.25) 0%, transparent 55%), radial-gradient(ellipse at 15% 80%, rgba(20,184,166,0.12) 0%, transparent 40%)",
          }}
          aria-hidden
        />
        {/* Compass watermark */}
        <CompassIcon
          className="pointer-events-none absolute -right-8 -top-8 size-48 rotate-12 text-white/[0.03]"
          aria-hidden
        />

        <CardContent className="relative z-10 p-5 md:p-7">
          {/* Top: Live + kind */}
          <div className="mb-4 flex items-center gap-3">
            <Badge variant="overdue" dotted pulse className="uppercase tracking-widest">
              Live
            </Badge>
            <span className="text-[0.625rem] font-semibold uppercase tracking-[0.07em] text-white/45">
              {ACTIVE.kind}
            </span>
          </div>

          {/* Title */}
          <h2 className="mb-1 font-heading text-xl font-bold leading-tight tracking-tight md:text-2xl">
            {ACTIVE.name}
          </h2>

          {/* Route */}
          <p className="mb-5 flex items-center gap-1.5 text-sm text-white/60">
            <FlagIcon className="size-3.5 shrink-0 text-white/30" aria-hidden />
            {ACTIVE.departurePort}
            <ArrowRightIcon className="size-3 shrink-0 text-white/25" aria-hidden />
            <FlagIcon className="size-3.5 shrink-0 text-white/30" aria-hidden />
            {ACTIVE.arrivalPort}
          </p>

          {/* Progress */}
          <div className="mb-5">
            <div className="mb-2 flex items-center justify-between text-xs text-white/50">
              <span>
                Day <span className="font-semibold text-white">{dayNum}</span> of {totalDays}
              </span>
              <span className="tabular-nums">
                <span className="font-semibold text-white">{ACTIVE.distanceCovered.toLocaleString()}</span>
                {" / "}
                {ACTIVE.totalDistance.toLocaleString()} nm
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-shallow-500 to-shallow-300"
                initial={{ width: 0 }}
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 1.4, ease: EASE_OUT, delay: 0.3 }}
              />
            </div>
            <p className="mt-1 text-right text-[0.625rem] tabular-nums text-white/35">{progressPct}% of route</p>
          </div>

          {/* Bottom: crew + vessel + CTA */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {/* Crew stack */}
              <div className="flex items-center">
                {ACTIVE.crew.map((m, i) => (
                  <Avatar
                    key={m.initials}
                    size="sm"
                    className={cn("size-8 border-2 border-abyss-800", i > 0 && "-ml-2.5")}
                  >
                    <AvatarFallback className="bg-abyss-500 text-xs font-bold text-white">{m.initials}</AvatarFallback>
                  </Avatar>
                ))}
                {ACTIVE.crewOverflow > 0 && (
                  <Avatar size="sm" className="-ml-2.5 size-8 border-2 border-abyss-800">
                    <AvatarFallback className="bg-abyss-700 text-xs font-bold text-white/80">
                      +{ACTIVE.crewOverflow}
                    </AvatarFallback>
                  </Avatar>
                )}
              </div>
              {/* Vessel */}
              <p className="text-sm text-white/60">
                {ACTIVE.vessel.flag} <span className="font-semibold text-white">{ACTIVE.vessel.name}</span>
              </p>
            </div>

            {/* CTA */}
            <Button
              variant="ghost"
              size="sm"
              className="border border-white/20 text-white hover:bg-white/10 hover:text-white"
              nativeButton={false}
              render={<Link to="/expeditions/$expeditionSlug" params={{ expeditionSlug: ACTIVE.slug }} />}
            >
              View Expedition
              <ArrowRightIcon data-icon="inline-end" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function WeatherStrip() {
  return (
    <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }}>
      <Card size="sm">
        <CardContent className="py-3">
          <div className="flex items-center justify-between gap-3 overflow-x-auto">
            <div className="flex items-center gap-2">
              <div className="flex size-6 items-center justify-center rounded-md bg-abyss-50 dark:bg-abyss-800">
                <MapPinIcon className="size-3 text-abyss-500" />
              </div>
              <span className="whitespace-nowrap text-xs font-semibold text-muted-foreground">Position: 21°N 34°W</span>
            </div>
            <Separator orientation="vertical" className="!h-6" />
            {WEATHER.map((w) => {
              const Icon = w.Icon;
              return (
                <div key={w.label} className="flex items-center gap-1.5 whitespace-nowrap">
                  <Icon className={cn("size-3.5", w.color)} />
                  <span className="text-sm font-semibold tabular-nums">{w.value}</span>
                  <span className="text-xs text-muted-foreground">{w.unit}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function KPIGrid() {
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {KPI_DATA.map((kpi, i) => {
        const Icon = kpi.Icon;
        return (
          <motion.div key={kpi.label} {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 + i * 0.06 }}>
            <Card className="group relative overflow-hidden transition-shadow hover:shadow-md">
              {/* Top accent bar */}
              <div className={cn("absolute inset-x-0 top-0 h-[3px] rounded-t-xl", kpi.accentClass)} />
              <CardContent className="pt-6 pb-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[0.625rem] font-bold uppercase tracking-[0.04em] text-muted-foreground">
                      {kpi.label}
                    </p>
                    <p className="mt-1.5 font-heading text-[1.625rem] font-bold tabular-nums leading-none tracking-[-0.02em]">
                      <AnimatedNumber value={kpi.value} />
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">{kpi.unit}</p>
                  </div>
                  <div
                    className={cn(
                      "flex size-10 shrink-0 items-center justify-center rounded-xl transition-transform group-hover:scale-110",
                      kpi.iconBg,
                    )}
                  >
                    <Icon className={cn("size-5", kpi.iconColor)} />
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-1 text-xs font-medium text-shallow-600">
                  <TrendingUpIcon className="size-3 shrink-0" aria-hidden />
                  {kpi.delta}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        );
      })}
    </div>
  );
}

export function QuickActions() {
  return (
    <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.12 }} className="flex flex-wrap gap-2">
      {QUICK_ACTIONS.map((action) => {
        const Icon = action.Icon;
        return (
          <Link
            key={action.label}
            to={action.to}
            className={cn(
              "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
              action.bg,
              action.color,
            )}
          >
            <Icon className="size-4" />
            {action.label}
          </Link>
        );
      })}
    </motion.div>
  );
}

export function UpcomingVoyages() {
  return (
    <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.16 }}>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="font-heading text-[0.9375rem] font-semibold">Next Departures</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground"
            nativeButton={false}
            render={<Link to="/expeditions" />}
          >
            View all
            <ArrowRightIcon data-icon="inline-end" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          {VOYAGES.map((v) => (
            <Link
              key={v.slug}
              to="/expeditions/$expeditionSlug"
              params={{ expeditionSlug: v.slug }}
              className="group flex gap-4 rounded-lg border border-border bg-background p-3 transition-colors hover:border-abyss-300/40 hover:bg-muted/30"
            >
              {/* Date pill */}
              <div className="flex flex-col items-center justify-center rounded-lg bg-abyss-50 px-3 py-1.5 dark:bg-abyss-800">
                <span className="font-heading text-lg font-bold leading-none text-abyss-500">
                  {v.dates.split("–")[0]?.split(" ")[0]}
                </span>
                <span className="text-[0.625rem] font-medium uppercase text-muted-foreground">
                  {v.dates.split(" ").pop()}
                </span>
              </div>

              {/* Info */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-semibold">{v.name}</p>
                  <Badge variant={v.status} className="text-[0.625rem]">
                    {v.status === "open" ? "Open" : "Planning"}
                  </Badge>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {v.route} · {v.distance}
                </p>
                <div className="mt-2 flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">
                    {v.flag} {v.vessel}
                  </span>
                  <Separator orientation="vertical" className="!h-3" />
                  <span className="text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">{v.crewFilled}</span>/{v.crewTotal} crew
                  </span>
                  {/* Mini crew bar */}
                  <div className="h-1 w-12 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-shallow-500"
                      style={{ width: `${(v.crewFilled / v.crewTotal) * 100}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Arrow */}
              <div className="flex items-center">
                <ArrowRightIcon className="size-4 text-muted-foreground/40 transition-transform group-hover:translate-x-0.5 group-hover:text-abyss-500" />
              </div>
            </Link>
          ))}
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function CrewRoster() {
  return (
    <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.22 }}>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="font-heading text-[0.9375rem] font-semibold">Crew on Standby</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground"
            nativeButton={false}
            render={<Link to="/crew" />}
          >
            Full roster
            <ArrowRightIcon data-icon="inline-end" />
          </Button>
        </CardHeader>
        <CardContent className="pt-0">
          <motion.div className="divide-y divide-border" {...stagger} initial="initial" animate="animate">
            {CREW.map((m) => {
              const cfg = presenceConfig[m.presence];
              return (
                <motion.div
                  key={m.initials}
                  className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0"
                  variants={fadeUp}
                >
                  <div className="relative shrink-0">
                    <Avatar size="sm" className="size-9">
                      {m.avatar ? <AvatarImage src={m.avatar} /> : null}
                      <AvatarFallback className="bg-abyss-100 text-xs font-bold text-abyss-700 dark:bg-abyss-800 dark:text-abyss-200">
                        {m.initials}
                      </AvatarFallback>
                    </Avatar>
                    <span
                      className={cn(
                        "absolute -bottom-0.5 -right-0.5 size-3 rounded-full border-2 border-card",
                        cfg.dot,
                      )}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold leading-tight">{m.name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {m.role} · {m.cert}
                    </p>
                  </div>
                  <span className="shrink-0 text-[0.625rem] font-medium text-muted-foreground">{cfg.label}</span>
                </motion.div>
              );
            })}
          </motion.div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function FleetStatus() {
  return (
    <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }}>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="font-heading text-[0.9375rem] font-semibold">Fleet Status</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground"
            nativeButton={false}
            render={<Link to="/vessels" />}
          >
            Manage fleet
            <ArrowRightIcon data-icon="inline-end" />
          </Button>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="divide-y divide-border">
            {FLEET.map((v) => {
              const cfg = vesselConfig[v.status];
              return (
                <div key={v.name} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                  <div className={cn("flex size-9 shrink-0 items-center justify-center rounded-lg", cfg.iconBg)}>
                    <AnchorIcon className={cn("size-4", cfg.iconColor)} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{v.name}</p>
                    <p className="truncate text-xs text-muted-foreground">{v.type}</p>
                    {v.expedition && <p className="truncate text-xs font-medium text-abyss-500">{v.expedition}</p>}
                  </div>
                  <Badge variant={cfg.variant} {...(v.status === "at-sea" ? { dotted: true } : {})}>
                    {cfg.label}
                  </Badge>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function ActivityFeed() {
  return (
    <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.24 }}>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-[0.9375rem] font-semibold">Latest Signals</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-3">
            {ACTIVITIES.map((a, i) => {
              const Icon = a.Icon;
              return (
                <div
                  key={a.id}
                  className={cn("flex gap-3", i < ACTIVITIES.length - 1 && "border-b border-border pb-3")}
                >
                  <div className={cn("mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full", a.iconBg)}>
                    <Icon className={cn("size-3.5", a.iconColor)} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm leading-snug">{a.text}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{a.time}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ── DashboardPage ─────────────────────────────────────────────────────────────

export function DashboardPage() {
  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-5 p-4 md:p-6">
      {/* ── Greeting ── */}
      <motion.div {...fadeUp} className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex size-11 items-center justify-center rounded-xl bg-abyss-500 shadow-lg shadow-abyss-500/20">
            <SailboatIcon className="size-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-xl font-bold tracking-tight md:text-[1.375rem]">{greeting(USER_NAME)}</h1>
            <p className="text-sm text-muted-foreground">{ORG_NAME}</p>
          </div>
        </div>
        <Button nativeButton={false} render={<Link to="/expeditions/new" />}>
          <PlusIcon data-icon="inline-start" />
          Plan a Voyage
        </Button>
      </motion.div>

      {/* ── Quick actions ── */}
      <QuickActions />

      {/* ── Live expedition hero ── */}
      <ActiveHero />

      {/* ── Weather strip ── */}
      <WeatherStrip />

      {/* ── KPI row ── */}
      <KPIGrid />

      {/* ── Content grid ── */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Left — 2 cols */}
        <div className="space-y-5 lg:col-span-2">
          <UpcomingVoyages />
          <CrewRoster />
        </div>

        {/* Right — 1 col */}
        <div className="space-y-5">
          <FleetStatus />
          <ActivityFeed />
        </div>
      </div>
    </div>
  );
}
